import { z } from "zod";
import type { ConnectorIconVariant } from "./artifacts-shared.ts";
import { ACCESS_VOCABULARY_PARAM } from "./auth.ts";
import type { ConnectorCategory } from "./connectorCategories.ts";
import type { ImapPresetTileId } from "./imap.ts";
import { phoneNumberSchema } from "./phone.ts";

export const connectorProvider = z.string();

export type ConnectorProvider = z.infer<typeof connectorProvider>;

/** Connect-failure code for a Slack login another okthx account already holds. */
export const SLACK_ALREADY_CONNECTED = "slack_already_connected";

/** Connect-failure code for a Teams login another okthx account already holds. */
export const TEAMS_ALREADY_CONNECTED = "teams_already_connected";

/** The help-center guide for a Teams admin whose tenant policy blocks okthx. */
export const TEAMS_ADMIN_GUIDE_URL =
  "https://www.okthx.com/articles/how-to-add-okthx-to-microsoft-teams";

/**
 * Connect-failure code for Microsoft refusing Teams consent: the user declined,
 * or their organization requires an admin to approve okthx. Entra reports both
 * the same way, so the copy covers both.
 */
export const TEAMS_CONSENT_DENIED = "teams_consent_denied";

/** Public API path that sends a tenant admin to approve okthx for their organization. */
export const TEAMS_ADMIN_CONSENT_PATH = "/api/p/teams/admin-consent";

/**
 * Providers whose login belongs to at most one okthx account, each with the
 * connect-failure code for a login another account holds. A sender on these
 * providers has to resolve to exactly one account.
 *
 * Each key also gets a unique index on `provider_account_id` in
 * `schemas/connectors.ts`, so adding one generates a migration.
 */
export const LOGIN_ALREADY_CONNECTED = {
  slack: SLACK_ALREADY_CONNECTED,
  teams: TEAMS_ALREADY_CONNECTED,
} as const;

export type OneAccountPerLoginProvider = keyof typeof LOGIN_ALREADY_CONNECTED;

/** True when `provider`'s login belongs to at most one okthx account. */
export function isOneAccountPerLoginProvider(
  provider: string,
): provider is OneAccountPerLoginProvider {
  return Object.hasOwn(LOGIN_ALREADY_CONNECTED, provider);
}

/**
 * WhatsApp's catalog identity. The clients build its tile locally and the
 * connectors module lists it for the agent, so all three must agree on these —
 * the slug is what the agent's `/connectors?connect=whatsapp` link highlights.
 */
export const WHATSAPP_CATALOG_ID = "provider:whatsapp";
export const WHATSAPP_CATALOG_SLUG = "whatsapp";

/**
 * The Text Messages connector: the account's verified phone, shown as a
 * connector. Not a `connectorSummary`, because it has no connector row behind
 * it, and a client that predates it would draw an unknown row with a reconnect
 * link that goes nowhere.
 */
export const textMessagesConnector = z.object({
  /** The verified number, E.164. */
  phoneNumber: z.string(),
  /**
   * Why Okthx's texts to the number are being refused, or null when they are
   * not. `optedOut`: the person texted STOP and lifts it by texting START.
   * `replyLimit`: they have never texted Okthx and the texts allowed before a
   * reply are spent; any text from them lifts it.
   */
  needsAttention: z.enum(["replyLimit", "optedOut"]).nullable(),
  /** The number Okthx texts from, for the person to text back. */
  assistantNumber: z.string(),
  /**
   * Why removing the number would be refused, or null when it would not.
   * `phoneSignIn`: the number is the account's only way to sign in.
   * `otpRequired`: the number was never proven by a code.
   */
  removeBlockedReason: z.enum(["phoneSignIn", "otpRequired"]).nullable(),
});
export type TextMessagesConnector = z.infer<typeof textMessagesConnector>;

/** The refusal to remove a number that is the account's only way to sign in. */
export const PHONE_ONLY_SIGN_IN = {
  error: "You sign in with this number, so it can't be removed.",
  code: "PHONE_ONLY_SIGN_IN",
} as const;

export const connectorVisibility = z.enum(["all", "beta", "internal"]);
export type ConnectorVisibility = z.infer<typeof connectorVisibility>;

/**
 * What a connect card's re-auth adds to a connector: one kind of access, on the
 * account the user already signed in with.
 */
export const reauthAdditionSchema = z.enum(["calendar", "email", "contacts"]);
export type ReauthAddition = z.infer<typeof reauthAdditionSchema>;

/**
 * The additions a re-auth asks for, as carried in its OAuth state: a list, so
 * one consent can add several kinds. A single value still parses, so a state
 * signed before lists existed finishes as it began.
 */
export const reauthAdditionsSchema = z
  .union([reauthAdditionSchema, z.array(reauthAdditionSchema).min(1)])
  .transform((value) => [...new Set(Array.isArray(value) ? value : [value])]);

export const connectorSummary = z.object({
  id: z.number(),
  provider: connectorProvider,
  email: z.string().nullable(),
  displayName: z.string(),
  needsAuth: z.boolean(),
  isLogin: z.boolean(),
  supportsCalendar: z.boolean(),
  /** True when the connector can read/send mail — a login or email-inbox connector. */
  supportsEmail: z.boolean(),
  /**
   * True when the connector holds what its catalog row promises — a Google
   * login connector with no calendar access does not serve "Google Calendar".
   * A client lists a connector under its catalog row only when this holds,
   * because a Connected row offers no way to connect.
   *
   * Optional so a client newer than its server still parses. Absent means the
   * server predates the field, and a client must then read it as true, as
   * every client did before it existed.
   */
  servesCatalogItem: z.boolean().optional(),
  /**
   * The access a Google or Microsoft connector's own re-auth can still add,
   * mail first. Settings offers a connect for each.
   *
   * Optional so a client newer than its server still parses. Absent means the
   * server predates the field, and a client must then offer nothing.
   */
  missingAccess: z.array(reauthAdditionSchema).optional(),
  /**
   * The connected service's homepage, for clients that draw a favicon when no
   * hand-drawn brand mark exists. Present only for the admin-registered long
   * tail (MCP servers, OpenAPI specs); first-party providers ship a mark and
   * don't need it.
   */
  websiteUrl: z.string().optional(),
  /**
   * True for a connection to a custom MCP server this account registered for
   * itself. Clients mark it as custom and as visible only to this user.
   */
  custom: z.boolean().optional(),
  /**
   * Microsoft Teams only. Whether okthx's Teams app is installed for this
   * person, and the link that opens Teams' dialog to add it.
   *
   * Optional so a client newer than its server still parses. Absent means the
   * connector isn't Teams or the server predates the field; a client then
   * offers no "Add to Teams".
   */
  teams: z.object({ botInstalled: z.boolean(), installUrl: z.string() }).optional(),
});

export type ConnectorSummary = z.infer<typeof connectorSummary>;

export const listConnectorsResponse = z.object({
  connectors: z.array(connectorSummary),
});

export type ListConnectorsResponse = z.infer<typeof listConnectorsResponse>;

export type ConnectorCatalogConnectKind =
  | "link"
  | "api_key"
  | "credential_json"
  /** An IMAP mailbox: address, app password, and optional server settings. */
  | "imap_credentials"
  /**
   * The entry point for registering a custom MCP server: the client opens its
   * own add flow (URL, auth, name) rather than following a connect path.
   */
  | "custom_mcp";
export type ConnectorCatalogCredentialKind = "api_key" | "bearer_token" | "credential_json";

export type ConnectorCatalogItem = {
  /** Opaque stable id for rendering and query caching. */
  id: string;
  name: string;
  slug: string;
  description: string;
  websiteUrl: string;
  /**
   * Connector family for the integrations we ship a brand mark for (`google`,
   * `microsoft`, `slack`, …). Null for anything drawn from `websiteUrl`'s
   * favicon instead: the admin-registered long tail, and the built-in IMAP
   * preset tiles, whose ISP marks we do not hand-draw.
   */
  provider: string | null;
  /**
   * Which product mark to draw, for a provider fronting several: connecting
   * Google as a calendar source shows the Calendar mark, as a mailbox the
   * Gmail mark. Absent when `provider` is null.
   */
  iconVariant?: ConnectorIconVariant;
  /**
   * True when connecting again is meaningful — a workspace can monitor
   * several mailboxes, so those tiles keep offering "Connect" rather than
   * reflecting a single connected state.
   */
  repeatable?: boolean;
  /** This integration currently supports setup and use from the web app only. */
  webOnly?: boolean;
  /**
   * A custom MCP server this account registered for itself. Only this account
   * sees it; clients badge it as custom and offer rename and remove.
   */
  custom?: boolean;
  connected: boolean;
  connectorId: number | null;
  needsAuth: boolean;
  /**
   * Category slugs this connector is tagged with (from the `connector_categories`
   * vocabulary). Zero or more; an empty list means the connector shows only under
   * "All" in the gallery, never under a category filter.
   */
  categories: string[];
  /**
   * The connector's built-in area id (`routineAreas.ts`), or null for none, and
   * the role ids it suits (empty: every role). The Connectors page groups and
   * filters by them, as the Routines page does. Strings, not the enums, as on a
   * routine: an app build must cope with an area added after it shipped.
   * Optional so a client newer than its server still reads the item.
   */
  area?: string | null;
  roles?: string[];
  /** How the client should initiate the connection. */
  connectKind: ConnectorCatalogConnectKind;
  /** Which inline credential shape the client should label when connectKind is not "link". */
  credentialKind?: ConnectorCatalogCredentialKind | null;
  /** Relative API path for OAuth/no-auth redirects. Null when credentials are submitted inline. */
  connectPath: string | null;
  /** Relative API path for inline credentials. Null when connectPath is used. */
  credentialSubmitPath: string | null;
  /**
   * Set on an IMAP tile that stands for one known provider, carrying the setup
   * steps its connect card shows and the preset its address must belong to.
   *
   * Null on "Other email inbox", which detects the provider from the typed
   * address and offers the Advanced form. Clients also read its presence as
   * "this is a preset tile" — native filters on it, rather than on a hardcoded
   * list of ids that a seventh preset would silently fall out of.
   */
  imapSetup?: ImapCatalogSetup | null;
};

/** The provider-specific half of an IMAP preset tile. See `ConnectorCatalogItem.imapSetup`. */
export type ImapCatalogSetup = {
  presetId: ImapPresetTileId;
  /** Ordered instructions for getting a password that works over IMAP. */
  setupSteps: readonly string[];
  /** The provider page step one names, for a form that can offer it as a link. */
  setupUrl: string;
  /** What this provider calls the secret, for the form's field label. */
  passwordLabel: string;
};

export type ListConnectorCatalogResponse = {
  items: ConnectorCatalogItem[];
  /** The category vocabulary, for the gallery's filter list. Items reference
   *  these by `slug`; an item slug absent here is a since-deleted category. */
  categories: ConnectorCategory[];
};

/** Admin: the category vocabulary, for the assignment multi-select. */
export type ListConnectorCategoriesResponse = { categories: ConnectorCategory[] };

export const adminConnectorCatalogKind = z.enum(["mcp", "openapi"]);
export type AdminConnectorCatalogKind = z.infer<typeof adminConnectorCatalogKind>;

export const bulkUpdateAdminConnectorVisibilityReq = z.object({
  items: z
    .array(
      z.object({
        kind: adminConnectorCatalogKind,
        id: z.number().int().positive(),
      }),
    )
    .min(1),
  visibility: connectorVisibility,
});

export type BulkUpdateAdminConnectorVisibilityReq = z.infer<
  typeof bulkUpdateAdminConnectorVisibilityReq
>;

export type BulkUpdateAdminConnectorVisibilityResponse = {
  updated: number;
};

export const deleteAdminConnectorCatalogItemReq = z.object({
  kind: adminConnectorCatalogKind,
  id: z.number().int().positive(),
  confirmation: z.literal("YES DELETE"),
});

export type DeleteAdminConnectorCatalogItemReq = z.infer<typeof deleteAdminConnectorCatalogItemReq>;

export type DeleteAdminConnectorCatalogItemResponse = {
  ok: true;
  deletedConnectors: number;
};

export const updateConnectorReq = z.object({
  displayName: z.string().optional(),
});

export type UpdateConnectorReq = z.infer<typeof updateConnectorReq>;

// ── Admin: recent connections + expire-auth ─────────────────────────

/** A row in the admin "recent connections" table (one per connector). */
export const connectorAdminRow = z.object({
  id: z.number(),
  accountId: z.number(),
  /** The okthx account's identity (signup email / phone) — the common owner
   *  label shown across every provider. */
  accountDisplayName: z.string(),
  provider: connectorProvider,
  /** Provider-specific label for the connector — OAuth email for Google /
   *  Microsoft, workspace/team name for Slack. */
  displayName: z.string(),
  needsAuth: z.boolean(),
  grantedScopes: z.array(z.string()).nullable(),
  /** When the connector was first created (ISO). */
  createdAt: z.string(),
  /** Last touched — reconnect or token refresh (ISO). */
  updatedAt: z.string().nullable(),
});

export type ConnectorAdminRow = z.infer<typeof connectorAdminRow>;

export const listConnectorsAdminResponse = z.object({
  connectors: z.array(connectorAdminRow),
});

export type ListConnectorsAdminResponse = z.infer<typeof listConnectorsAdminResponse>;

/** Expire auth for every connector of a provider (forces reconnect). */
export const expireConnectorsBulkReq = z.object({
  provider: connectorProvider,
});

export type ExpireConnectorsBulkReq = z.infer<typeof expireConnectorsBulkReq>;

export const expireConnectorsBulkResponse = z.object({
  count: z.number(),
});

export type ExpireConnectorsBulkResponse = z.infer<typeof expireConnectorsBulkResponse>;

/** WhatsApp connector — link/verification state plus the okthx deep-link fields. */

export const whatsappConnectorStatusResponse = z.object({
  /** True when a WhatsApp connector row exists (linked or pending). */
  connected: z.boolean(),
  phoneNumber: z.string().nullable(),
  /** True once the number is verified (set by the device link); only then does it route. */
  verified: z.boolean(),
  /** True once the user's device is linked (the connector has a session identity). */
  linked: z.boolean(),
  /** The okthx company number in E.164, for "message okthx" surfaces. */
  okthxNumber: z.string().nullable(),
  /** wa.me deep link that opens a WhatsApp chat with the okthx number. */
  waMeLink: z.string().nullable(),
});

export type WhatsappConnectorStatusResponse = z.infer<typeof whatsappConnectorStatusResponse>;

/**
 * WhatsApp device link — a user links their personal WhatsApp as a device
 * (QR / pairing code) via the first-party session host, which verifies the
 * number, syncs the archive, and enables approval-gated personal sends.
 */

export const whatsappSyncLinkReq = z.object({
  /**
   * When present, request a pairing CODE for this E.164 number (entered on the
   * phone under Linked Devices) instead of a QR. Absent = QR flow.
   */
  phoneNumber: phoneNumberSchema.optional(),
  /**
   * The code already shown to the user didn't work — mint a new one instead of
   * re-serving it. Without this a retry gets the same string back (the host
   * resumes an in-flight code by design, so a remount can't invalidate a code
   * being typed) and fails identically. The host reuses the number the attempt
   * was started with, so a client that lost it still stays on the code flow.
   */
  refresh: z.boolean().optional(),
});
export type WhatsappSyncLinkReq = z.infer<typeof whatsappSyncLinkReq>;

export const whatsappSyncLinkResponse = z.discriminatedUnion("mode", [
  /** Poll `GET /status` for the QR string, which the host produces asynchronously. */
  z.object({ mode: z.literal("qr") }),
  z.object({ mode: z.literal("pairing"), pairingCode: z.string() }),
  z.object({ mode: z.literal("already_connected") }),
]);
export type WhatsappSyncLinkResponse = z.infer<typeof whatsappSyncLinkResponse>;

export const whatsappSyncStatusResponse = z.object({
  /** True when a sync connector row exists (linked or pending). */
  connected: z.boolean(),
  /** Session link status from the host registry. */
  status: z.enum(["pending_link", "linking", "connected", "logged_out", "error"]),
  phoneNumber: z.string().nullable(),
  /**
   * The live link fields (`qr`, `pairingCode`, `pairingCodeIssuedAt`,
   * `linkMode`) are optional, not just nullable: `null` means the host
   * reported no code, while an omitted field means the host was unreachable
   * and its state is unknown. A client must treat those differently — a
   * present `null` blanks the code, an absent field must not — so the status
   * handler omits them when the host read fails rather than sending `null`.
   */
  /** The QR string to render while `status === "linking"`. */
  qr: z.string().nullish(),
  /**
   * The pairing code to re-show while a pairing-mode link is in flight. The
   * user must leave the client to type the code into WhatsApp, so a remount
   * on return re-reads it from here instead of restarting the link (a new
   * `POST /link` invalidates the code being typed).
   */
  pairingCode: z.string().nullish(),
  /**
   * ISO timestamp of the pairing code's issue. Informational for now; a
   * client may use it to age a code out or show a countdown.
   */
  pairingCodeIssuedAt: z.string().nullish(),
  /**
   * Which link flow is in flight, so `linking` + `qr: null` is readable:
   * `"pairing"` = re-show the code; `"qr"` = the QR string hasn't arrived yet.
   */
  linkMode: z.enum(["qr", "pairing"]).nullish(),
  lastError: z.string().nullable(),
});
export type WhatsappSyncStatusResponse = z.infer<typeof whatsappSyncStatusResponse>;

/**
 * Coarse health of a WhatsApp session, derived from its recent socket-close
 * history and reachout-timelock state. Not a delivery guarantee — an early
 * warning the admin panel surfaces so a degrading number gets attention before
 * it is hard-banned. `at_risk` warrants a human look (re-link, pause, or IP
 * change); `watch` is a flapping-but-recovering socket.
 */
export const whatsappSessionHealthLevel = z.enum(["healthy", "watch", "at_risk"]);
export type WhatsappSessionHealthLevel = z.infer<typeof whatsappSessionHealthLevel>;

export const whatsappSessionHealth = z.object({
  level: whatsappSessionHealthLevel,
  /** Adverse socket closes since the last clean connect. */
  recentErrorCount: z.number(),
  /** ISO time a `463` reachout timelock lifts, or null when none is active. */
  timelockUntil: z.string().nullable(),
  /** Human-readable reasons behind a non-`healthy` level, for the admin panel. */
  reasons: z.array(z.string()),
});
export type WhatsappSessionHealth = z.infer<typeof whatsappSessionHealth>;

/**
 * Status of the okthx company-number transport session (admin-portal only).
 * Same session states as personal sync, but keyed on the linked device's
 * `selfJid` rather than a per-user phone number.
 */
export const whatsappOkthxStatusResponse = z.object({
  connected: z.boolean(),
  status: z.enum(["pending_link", "linking", "connected", "logged_out", "error"]),
  selfJid: z.string().nullable(),
  qr: z.string().nullable(),
  lastError: z.string().nullable(),
  /** Session health signal, or null before a transport connector is linked. */
  health: whatsappSessionHealth.nullable(),
});
export type WhatsappOkthxStatusResponse = z.infer<typeof whatsappOkthxStatusResponse>;

/**
 * A comma list of access ids (`email,calendar`), deduped. Only known values
 * are accepted and the list is never empty, so a client can never start a
 * consent that silently does less.
 */
const accessIdListSchema = z
  .string()
  .transform((value) =>
    value
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean),
  )
  .pipe(z.array(reauthAdditionSchema).min(1))
  .transform((ids) => [...new Set(ids)]);

/**
 * The query of a connector's re-auth start route. `add` names what the re-auth
 * adds, as one value or a comma list (`add=email,calendar`); absent is a plain
 * re-auth.
 */
export const reauthStartQuerySchema = z.object({
  add: accessIdListSchema.optional(),
});

/**
 * The query of the `/add-connector` and `/add-email-inbox` start routes.
 * `access` is what the user ticked on the access screen, and narrows the
 * route's own scopes; absent asks for all of them. `access_v` is read as on
 * the login routes: any value but the current version means the old
 * vocabulary.
 */
export const connectorAddStartQuerySchema = z.object({
  access: accessIdListSchema.optional(),
  [ACCESS_VOCABULARY_PARAM]: z.string().optional(),
});
